import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Shield, BarChart3 } from "lucide-react";
import { Button } from "./ui/button";

const CookieConsent = () => {
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) {
      setShow(false);
      return;
    }
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setShow(true);
    }
  }, [location.pathname]);

  const handleAcceptAll = () => {
    localStorage.setItem("cookieConsent", "all");
    localStorage.setItem("marketingConsent", "true");
    setShow(false);
    // Reload so Pixel can init on next render
    window.dispatchEvent(new Event("consentChanged"));
  };

  const handleEssentialOnly = () => {
    localStorage.setItem("cookieConsent", "essential");
    localStorage.setItem("marketingConsent", "false");
    setShow(false);
    window.dispatchEvent(new Event("consentChanged"));
  };

  const handleClose = () => {
    localStorage.setItem("cookieConsent", "essential");
    localStorage.setItem("marketingConsent", "false");
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 animate-in slide-in-from-bottom"
    >
      <div className="bg-card border border-border rounded-lg shadow-lg p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="font-semibold text-sm">Privacy & Cookies</h3>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close"
            data-testid="cookie-consent-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground mb-3">
          We use essential cookies to keep you signed in. You can also opt in to
          marketing cookies to help us measure ad performance.
        </p>

        {showDetails && (
          <div className="text-xs text-muted-foreground space-y-2 mb-3 border-t border-border pt-3">
            <div className="flex items-start gap-2">
              <Shield className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div>
                <strong>Essential</strong> — session, authentication, security.
                Always active.
              </div>
            </div>
            <div className="flex items-start gap-2">
              <BarChart3 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div>
                <strong>Marketing</strong> — Meta (Facebook) Pixel for
                conversion measurement. Only with your consent.
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" onClick={handleAcceptAll} data-testid="cookie-consent-accept-all">
            Accept All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleEssentialOnly}
            data-testid="cookie-consent-essential-only"
          >
            Essential Only
          </Button>
          {!showDetails && (
            <button
              onClick={() => setShowDetails(true)}
              className="text-xs text-muted-foreground underline hover:text-foreground ml-1"
            >
              Details
            </button>
          )}
          <Link to="/privacy" className="ml-auto">
            <Button variant="ghost" size="sm" className="text-xs h-7 px-2">
              Privacy Policy
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
